import React, { FC, useCallback, useMemo } from "react";
import { Pressable } from "react-native";
import { generatePath, useNavigate } from "react-router";
import { COLOR, Box, Thumbnail, View, Divider } from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import "@lookiero/sty-psp-ui";
import { ButtonIcon, Text } from "@lookiero/sty-psp-ui";
import { Order } from "../../../../../../domain/entities/order/Order";
import { ItemStatus } from "../../../../../../domain/entities/product/Product";
import { ItemLockSVG } from "../../../../components/itemLockSVG/ItemLockSVG";
import { Label } from "../../../../components/label/Label";
import { useFormatCurrency } from "../../../../hooks/useFormatCurrency";
import { useStaticInfo } from "../../../../hooks/useStaticInfo";
import { DOMAIN, I18nMessages } from "../../../../i18n/i18n";
import { Routes } from "../../../../routing/routes";
import { cdnImageUrl } from "../../../../shared/util/cdnImageUrl";
import { financial } from "../../../../shared/util/financial/financial";
import {
  MAX_COLUMNS,
  INVALID_ITEM_STATUS,
  STATUS_COLOR,
  IMAGE_WIDTH,
  IMAGE_HEIGHT,
  CompletedOrderStatus,
} from "./OrderCard.definition";
import { style as orderCardStyle } from "./OrderCard.styles";

interface OrderCardProps {
  readonly order: Order;
  readonly formatDate: (date: string) => string;
}

const OrderCard: FC<OrderCardProps> = ({ order, formatDate }) => {
  const navigate = useNavigate();
  const { basePath } = useStaticInfo();

  const style = useMemo(() => orderCardStyle(), []);

  const { datePlanned, totalPrice, items, serviceStatus, orderId, reference } = order;
  const formattedDate = formatDate(datePlanned);
  const formatCurrency = useFormatCurrency();

  const hasMoreItems = items.length > MAX_COLUMNS;
  const mainProducts = hasMoreItems ? items.slice(0, MAX_COLUMNS - 1) : items;

  const statusKey = `ORDERS_STATUS_${serviceStatus}` as keyof typeof I18nMessages;
  const statuxText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages[statusKey],
  });
  const statusName = I18nMessages[statusKey] ? statuxText : "";
  const statusLabelColor = serviceStatus ? STATUS_COLOR[serviceStatus as CompletedOrderStatus] : STATUS_COLOR.CANCELLED;

  const ordersReferenceText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.ORDERS_REFERENCE,
    values: { refOrder: reference },
  });

  const handleOnPress = useCallback(() => {
    navigate(generatePath(`${basePath}/${Routes.ORDER}`, { orderId }));
  }, [basePath, navigate, orderId]);

  return (
    <>
      <Pressable key={orderId} style={style.order} testID={`order-component-${serviceStatus}`} onPress={handleOnPress}>
        <View>
          <View style={style.header}>
            <View>
              <Text level={1} style={style.date} variant="detail">
                {formattedDate}
              </Text>
              <Text level={2}>{formatCurrency({ amount: financial(totalPrice || 0) })}</Text>
            </View>
            <ButtonIcon name="arrow_right" onPress={handleOnPress} />
          </View>

          {items.length > 0 ? (
            <Box style={style.items}>
              {mainProducts.map(({ itemStatus, imageUrl, itemId }) => {
                const isDisabledItem = INVALID_ITEM_STATUS.includes(itemStatus);
                const isUnavailableItem = itemStatus === ItemStatus.UNAVAILABLE;
                const imageSrc = cdnImageUrl({ imageUrl, width: IMAGE_WIDTH });

                return (
                  <View
                    key={`${orderId}-${itemId}`}
                    style={[
                      style.item,
                      isDisabledItem && !isUnavailableItem ? style.disabledItem : undefined,
                      isDisabledItem && isUnavailableItem ? style.unavailableItem : undefined,
                    ]}
                  >
                    <Thumbnail
                      height={IMAGE_HEIGHT}
                      src={imageSrc}
                      srcSet={imageSrc}
                      style={isUnavailableItem ? style.unavailableItemImage : undefined}
                      width={IMAGE_WIDTH}
                      backgroundColor={
                        isDisabledItem && isUnavailableItem ? COLOR.BG_ACTION_PRIMARY_DISABLED : undefined
                      }
                    />
                    {isDisabledItem && !isUnavailableItem && <ItemLockSVG height={IMAGE_HEIGHT} width={IMAGE_WIDTH} />}
                  </View>
                );
              })}
              {hasMoreItems && (
                <View style={style.otherProductsWrapper}>
                  <Text level={1} style={style.otherProductsText} variant="detail">
                    {`+${items.length - mainProducts.length}`}
                  </Text>
                </View>
              )}
            </Box>
          ) : null}

          <View style={style.footer}>
            <View>
              <Text level={1} style={style.referenceText} variant="detail">
                {ordersReferenceText}
              </Text>
            </View>
            <Label color={statusLabelColor} textColor={COLOR.TEXT}>
              {statusName}
            </Label>
          </View>
        </View>
      </Pressable>
      <Divider color={COLOR.BG_PRIMARY_MEDIUM_LIGHT} />
    </>
  );
};

export { OrderCard };
